import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    UserPlusIcon,
    CalendarDaysIcon,
    BeakerIcon,
    BanknotesIcon,
    ShoppingCartIcon,
    UsersIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const QuickActions = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();

    const pageEnabled = (key: string) => {
        if (user?.role === 'super_admin') return true;
        const page = user?.tenant_pages?.[key];
        return page ? page.enabled !== false && page.is_enabled !== false : false;
    };

    const actions = [
        {
            title: t('dashboard.quick_add_farmer'),
            icon: <UserPlusIcon className="w-8 h-8 text-white" />,
            color: 'bg-green-500',
            path: '/dashboard/farmers',
            page: 'farmers'
        },
        {
            title: t('dashboard.quick_schedule_training'),
            icon: <CalendarDaysIcon className="w-8 h-8 text-white" />,
            color: 'bg-blue-500',
            path: '/dashboard/training',
            page: 'training'
        },
        {
            title: t('dashboard.quick_create_compost'),
            icon: <BeakerIcon className="w-8 h-8 text-white" />,
            color: 'bg-amber-500',
            path: '/dashboard/compost',
            page: 'compost'
        },
        {
            title: 'Marketplace',
            icon: <BanknotesIcon className="w-8 h-8 text-white" />,
            color: 'bg-purple-500',
            path: '/marketplace/supplier',
            page: 'marketplace'
        },
        {
            title: t('dashboard.quick_view_orders'),
            icon: <ShoppingCartIcon className="w-8 h-8 text-white" />,
            color: 'bg-indigo-500',
            path: '/dashboard/orders',
            page: 'orders'
        },
    ];

    if (user?.role === 'project_manager') {
        actions.push({
            title: t('dashboard.quick_manage_users'),
            icon: <UsersIcon className="w-8 h-8 text-white" />,
            color: 'bg-slate-700',
            path: '/dashboard/users',
            page: 'users'
        });
    }

    const visibleActions = actions.filter((action) => pageEnabled(action.page));

    return (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {visibleActions.map((action, index) => (
                <button
                    key={index}
                    onClick={() => navigate(action.path)}
                    className="flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-all duration-200 hover:scale-105 group"
                >
                    <div className={`${action.color} p-4 rounded-xl mb-3 shadow-lg group-hover:scale-110 transition-transform duration-200`}>
                        {action.icon}
                    </div>
                    <span className="font-semibold text-slate-700 text-sm text-center">{action.title}</span>
                </button>
            ))}
            {visibleActions.length === 0 && (
                <div className="col-span-full rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm font-semibold text-slate-500">
                    No quick actions are enabled for this workspace yet.
                </div>
            )}
        </div>
    );
};

export default QuickActions;
